import React, { useEffect, useMemo } from 'react';
import { formatDistanceToNow, isAfter, isTomorrow, addDays, format } from 'date-fns';
import { Clock, Calendar, CalendarCheck2, Loader2, LucideIcon, AlertCircle, Info } from 'lucide-react';
import { cn } from '@shadcn/ui/lib/utils';
import { Button } from '@shadcn/ui/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shadcn/ui/components/ui/tooltip';
import { motion, AnimatePresence } from 'motion/react';

interface ActionDueNotificationProps {
    nextActionSuggestedAt?: string | null;
    snoozedUntil?: string | null;
    lastMessageSentAt?: Date | null;
    metadataLastUpdatedAt?: Date | null;
    className?: string;
    onSchedule?: () => void;
    onRefreshData?: () => void;
    messageJustSent?: boolean;
}

interface NotificationState {
    icon: LucideIcon;
    text: string;
    tooltipText?: string;
    tooltipDate?: Date;
    scheduleTooltip: string;
    scheduleIcon?: LucideIcon;
    animated?: boolean;
}

const NotificationPill: React.FC<{
    state: NotificationState;
    className?: string;
    onSchedule?: () => void;
}> = ({ state, className, onSchedule }) => {
    const {
        icon: Icon,
        text,
        tooltipText,
        tooltipDate,
        scheduleTooltip,
        scheduleIcon: ScheduleIcon = CalendarCheck2,
        animated,
    } = state;

    const content = (
        <div
            className={cn(
                'inline-flex items-center rounded-full',
                'bg-green-50 text-green-700 border border-green-200',
                'text-xs font-medium',
                className
            )}
        >
            <div className="flex items-center gap-1.5 px-3 pr-2 py-1.5">
                <Icon className={cn('h-3.5 w-3.5', Icon === Loader2 && 'animate-spin')} />
                <span>
                    {tooltipText && tooltipDate ? (
                        <>
                            {text}{' '}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="cursor-help">{tooltipText}</span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>{format(tooltipDate, 'EEEE, MMMM do')}</p>
                                </TooltipContent>
                            </Tooltip>
                        </>
                    ) : (
                        text
                    )}
                </span>
            </div>
            {onSchedule && (
                <>
                    <div className="w-px h-4 bg-green-200 mx-1" />
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 mr-1 hover:bg-green-100 text-green-600 hover:text-green-700 rounded-full"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSchedule();
                                }}
                            >
                                <ScheduleIcon className="h-3.5 w-3.5" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{scheduleTooltip}</p>
                        </TooltipContent>
                    </Tooltip>
                </>
            )}
        </div>
    );

    if (animated) {
        return (
            <AnimatePresence>
                <motion.div
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.95 }}
                    transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 30,
                        duration: 0.4,
                    }}
                >
                    {content}
                </motion.div>
            </AnimatePresence>
        );
    }

    return content;
};

export const ActionDueNotification: React.FC<ActionDueNotificationProps> = ({
    nextActionSuggestedAt,
    snoozedUntil,
    lastMessageSentAt,
    metadataLastUpdatedAt,
    className,
    onSchedule,
    onRefreshData,
    messageJustSent,
}) => {
    // Move now calculation inside useMemo to ensure it updates when props change
    const notificationState = useMemo(() => {
        const now = new Date();
        const tomorrow = addDays(now, 1);

        const messageSentWithoutUpdatedMetadata =
            lastMessageSentAt && metadataLastUpdatedAt && lastMessageSentAt > metadataLastUpdatedAt;

        // Show planning notification immediately when message is sent OR when message was sent without updated metadata
        const shouldShowPlanning = messageJustSent || messageSentWithoutUpdatedMetadata;

        if (shouldShowPlanning) {
            return {
                type: 'planning',
                state: {
                    icon: Loader2,
                    text: 'Planning next action...',
                    scheduleTooltip: 'Schedule next action',
                    scheduleIcon: Calendar,
                    animated: true,
                },
            };
        }

        // Check if no action is planned at all
        if (!nextActionSuggestedAt && !snoozedUntil) {
            return {
                type: 'no-action',
                state: {
                    icon: Info,
                    text: 'No action planned',
                    scheduleTooltip: 'Schedule a action for this contact',
                    scheduleIcon: Calendar,
                },
            };
        }

        // Determine which date to use and calculate if it's due
        let dueDate: Date | null = null;
        let notificationType: 'action' | 'snooze' | null = null;

        if (snoozedUntil) {
            const snoozeDate = new Date(snoozedUntil);
            if (isAfter(snoozeDate, now) && (isTomorrow(snoozeDate) || isAfter(snoozeDate, tomorrow))) {
                dueDate = snoozeDate;
                notificationType = 'snooze';
            }
        }

        if (nextActionSuggestedAt && !dueDate) {
            const actionDate = new Date(nextActionSuggestedAt);
            if (isAfter(actionDate, now) && (isTomorrow(actionDate) || isAfter(actionDate, tomorrow))) {
                dueDate = actionDate;
                notificationType = 'action';
            }
        }

        // Don't show notification if no relevant date or if it's today or in the past
        if (!dueDate || !notificationType) {
            return { type: 'none' };
        }

        const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        const timeText = daysUntilDue === 1 ? 'tomorrow' : `in ${formatDistanceToNow(dueDate)}`;
        const actionText = notificationType === 'snooze' ? 'Snoozed until' : 'Action planned';

        return {
            type: 'notification',
            state: {
                icon: Clock,
                text: actionText,
                tooltipText: timeText,
                tooltipDate: dueDate,
                scheduleTooltip: 'Reschedule action',
            },
        };
    }, [nextActionSuggestedAt, snoozedUntil, lastMessageSentAt, metadataLastUpdatedAt, messageJustSent]);

    // Set up polling when planning notification is active
    useEffect(() => {
        if (notificationState.type === 'planning' && onRefreshData) {
            const interval = setInterval(() => {
                onRefreshData();
            }, 2000); // Poll every 2 seconds

            return () => clearInterval(interval);
        }
    }, [notificationState.type, onRefreshData]);

    if (notificationState.type === 'none') {
        return null;
    }

    return <NotificationPill state={notificationState.state} className={className} onSchedule={onSchedule} />;
};
